import { FC, useCallback, useEffect, useMemo, useState } from "react";
import { useTable } from "spacetimedb/react";
import { tables } from "@module-bindings";
import type { Order } from "@module-bindings/types";
import { useGummiwormContext } from "@/context/GummiwormContext";
import {
  assetIdKey,
  formatAssetLabel,
  formatAssetValue,
  formatMarketPairLabel,
} from "@/lib/marketUtils";
import { useHandleCancelOrderRequest } from "@/hooks/useHandleCancelOrderRequest";
import { useNextNonce } from "@/hooks/useNextNonce";
import { toast } from "sonner";
import { TradingPaneState } from "./TradingPaneState";
import { ConnectWalletButton } from "@/components/WalletConnect/ConnectWalletButton";

type Tab = "orders" | "global";

const FRACTIONAL_BITS = 60n;

/** Convert a u128 fixed-point price (60 fractional bits) to a display string. */
function formatPrice(raw: bigint): string {
  const integer = raw >> FRACTIONAL_BITS;
  const fractional = raw & ((1n << FRACTIONAL_BITS) - 1n);
  if (fractional === 0n) return integer.toString();
  // Scale to 18 decimal digits, then trim trailing zeros
  const scaled = (fractional * 1_000_000_000_000_000_000n + (1n << FRACTIONAL_BITS) / 2n) >> FRACTIONAL_BITS;
  const fracStr = scaled.toString().padStart(18, "0").replace(/0+$/, "");
  return `${integer}.${fracStr}`;
}

function isTerminalOrderStatus(status: string): boolean {
  return status === "Filled" || status === "Cancelled";
}

function clampDisplayValue(value: string, maxSignificantDigits = 6): string {
  const normalized = value.replace(/,/g, "");
  const numeric = Number(normalized);

  if (!Number.isFinite(numeric)) {
    return value;
  }

  return new Intl.NumberFormat("en-US", {
    maximumSignificantDigits: maxSignificantDigits,
  }).format(numeric);
}

const statusLabels: Record<string, string> = {
  New: "Open",
  PartiallyFilled: "Partial",
  Filled: "Filled",
  Cancelled: "Cancelled",
};

const statusColors: Record<string, string> = {
  New: "text-sr-mint",
  PartiallyFilled: "text-sr-tangerine",
  Filled: "text-sr-lemon-muted",
  Cancelled: "text-sr-neutral-500",
};

const statusBadgeStyles: Record<string, string> = {
  New: "bg-sr-mint/10 text-sr-mint",
  PartiallyFilled: "bg-sr-tangerine/10 text-sr-tangerine",
  Filled: "bg-sr-lemon-muted/10 text-sr-lemon-muted",
  Cancelled: "bg-white/5 text-sr-neutral-400",
};

import type { Market } from "@module-bindings/types";

interface ActivityPanelProps {
  selectedMarket: Market | null;
}

export const ActivityPanel: FC<ActivityPanelProps> = ({ selectedMarket }) => {
  const [activeTab, setActiveTab] = useState<Tab>("orders");
  const [marketFilter, setMarketFilter] = useState(true); // true = this market only
  const { myAccountId, markets, accounts } = useGummiwormContext();
  // Open orders (all users) — for the "global" tab
  const [openOrders] = useTable(tables.orders.where(r => r.remaining.gt(0n)));
  // My orders (any status) — for the "my orders" tab; subscribes only when we have an account
  const myOrdersQuery = useMemo(
    () => myAccountId != null
      ? tables.orders.where((r: any) => r.owner.eq(myAccountId))
      : null,
    [myAccountId],
  );
  const [myOrdersRaw] = useTable(myOrdersQuery ?? tables.orders.where((r: any) => r.id.eq(0n)));
  const myOrders = myAccountId != null ? myOrdersRaw : [];

  const { mutateAsync: cancelOrder, isPending: isCancelling } = useHandleCancelOrderRequest();
  const nextNonce = useNextNonce();
  const [cancellingId, setCancellingId] = useState<string | null>(null);

  const myAccount = useMemo(
    () => myAccountId != null ? accounts.find((a) => a.accountId === myAccountId) : null,
    [accounts, myAccountId],
  );

  const handleCancel = useCallback(async (orderId: bigint) => {
    if (!myAccount) return;
    const idStr = orderId.toString();
    setCancellingId(idStr);
    try {
      await cancelOrder({
        orderId: idStr,
        nonce: nextNonce()!,
      });
      toast.success("Order cancelled");
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to cancel order");
    } finally {
      setCancellingId(null);
    }
  }, [myAccount, cancelOrder]);

  // Selected market's pair keys for filtering
  const selectedBaseKey = selectedMarket ? assetIdKey(selectedMarket.tradingPair.base) : null;
  const selectedQuoteKey = selectedMarket ? assetIdKey(selectedMarket.tradingPair.quote) : null;

  const isMarketMatch = (order: Order) => {
    if (!selectedBaseKey || !selectedQuoteKey) return true;
    return assetIdKey(order.pair.base) === selectedBaseKey &&
           assetIdKey(order.pair.quote) === selectedQuoteKey;
  };

  const displayOrders = useMemo(() => {
    const base = activeTab === "orders" ? myOrders : openOrders;
    return marketFilter && selectedMarket ? base.filter(isMarketMatch) : base;
  }, [activeTab, myOrders, openOrders, marketFilter, selectedBaseKey, selectedQuoteKey]); // eslint-disable-line react-hooks/exhaustive-deps

  // Sort by created descending (newest first)
  const sorted = useMemo(
    () => [...displayOrders].sort((a, b) => Number(b.created - a.created)),
    [displayOrders],
  );

  // Pagination
  const PAGE_SIZE = 30;
  const [page, setPage] = useState(0);
  const totalPages = Math.ceil(sorted.length / PAGE_SIZE);
  const paginated = useMemo(
    () => sorted.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE),
    [sorted, page],
  );
  // Reset to page 0 when filters change
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { setPage(0); }, [activeTab, marketFilter, selectedMarket?.key]);

  // Look up market for an order's trading pair
  const marketForOrder = (order: Order) =>
    markets.find((m) => {
      const ob = assetIdKey(order.pair.base);
      const oq = assetIdKey(order.pair.quote);
      const mb = assetIdKey(m.tradingPair.base);
      const mq = assetIdKey(m.tradingPair.quote);
      return ob === mb && oq === mq;
    });

  const renderCancelButton = (orderId: bigint, status: string, owner: bigint) => {
    if (
      (status !== "New" && status !== "PartiallyFilled") ||
      owner !== myAccountId
    ) {
      return null;
    }

    return (
      <button
        onClick={() => handleCancel(orderId)}
        disabled={isCancelling && cancellingId === orderId.toString()}
        className="text-xs text-sr-salmon hover:text-sr-salmon/80 transition-colors disabled:opacity-50"
      >
        {isCancelling && cancellingId === orderId.toString() ? "..." : "Cancel"}
      </button>
    );
  };

  const renderOrderValue = (
    value: string,
    status: string,
    className: string,
  ) => {
    const shouldClamp = isTerminalOrderStatus(status);
    const displayValue = shouldClamp ? clampDisplayValue(value) : value;
    const isClamped = displayValue !== value;

    return (
      <span
        className={`${className} ${isClamped ? "cursor-help" : ""}`}
        title={isClamped ? value : undefined}
      >
        {displayValue}
      </span>
    );
  };

  const emptyState = myAccountId == null
    ? {
        title: "Connect a wallet",
        message: "Connect a wallet to review your orders, fills, and cancellations.",
        action: <ConnectWalletButton />,
      }
    : activeTab === "orders"
      ? {
          title: marketFilter && selectedMarket ? "No orders in this market" : "No orders yet",
          message: marketFilter && selectedMarket
            ? "Your account does not have any orders for the selected pair yet."
            : "Your order history will appear here after your first trade.",
        }
      : {
          title: marketFilter && selectedMarket ? "No market activity yet" : "No orders available",
          message: marketFilter && selectedMarket
            ? "This market does not have any visible orders yet."
            : "There are no orders available to display right now.",
        };

  return (
    <section className="sr-panel overflow-hidden" aria-label="Activity and orders">
      {/* Tabs */}
      <div className="flex items-center justify-between border-b border-sr-border-subtle px-5">
        <div className="flex" role="tablist" aria-label="Activity tabs">
          {([
            ["orders", "My Orders"],
            ["global", "All Orders"],
          ] as const).map(([key, label]) => (
            <button
              key={key}
              role="tab"
              aria-selected={activeTab === key}
              onClick={() => setActiveTab(key)}
              className={`sr-focus-ring px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                activeTab === key
                  ? "text-sr-lemon-muted border-sr-lemon-muted"
                  : "text-sr-neutral-500 border-transparent hover:text-sr-neutral-300"
              }`}
            >
              {label}
            </button>
          ))}
        </div>
        {selectedMarket && (
          <div className="flex items-center gap-1 bg-sr-surface rounded-full p-0.5">
            <button
              onClick={() => setMarketFilter(true)}
              className={`sr-focus-ring px-2.5 py-0.5 rounded-full text-[10px] font-medium transition-colors ${
                marketFilter
                  ? "bg-sr-surface-raised text-white"
                  : "text-sr-neutral-500 hover:text-sr-neutral-400"
              }`}
            >
              This market
            </button>
            <button
              onClick={() => setMarketFilter(false)}
              className={`sr-focus-ring px-2.5 py-0.5 rounded-full text-[10px] font-medium transition-colors ${
                !marketFilter
                  ? "bg-sr-surface-raised text-white"
                  : "text-sr-neutral-500 hover:text-sr-neutral-400"
              }`}
            >
              All
            </button>
          </div>
        )}
      </div>

      {/* Mobile list */}
      <div className="flex flex-col gap-3 p-3 md:hidden">
        {sorted.length === 0 && (
          <TradingPaneState
            compact
            eyebrow={activeTab === "orders" ? "My Orders" : "Market Activity"}
            title={emptyState.title}
            message={emptyState.message}
            action={emptyState.action}
            className="rounded-2xl border border-white/[0.05] bg-black/10"
          />
        )}
        {paginated.map((order) => {
          const market = marketForOrder(order);
          const pairLabel = market
            ? formatMarketPairLabel(market)
            : `${formatAssetLabel(order.pair.base)}/${formatAssetLabel(order.pair.quote)}`;
          const status = order.status?.tag ?? (
            order.remaining === 0n ? "Filled"
            : order.remaining < order.originalAmount ? "PartiallyFilled"
            : "New"
          );
          const isBid = order.side.tag === "Bid";
          const amountAsset = isBid ? order.pair.quote : order.pair.base;
          const createdAt =
            order.created > 0n
              ? new Date(Number(order.created)).toLocaleString()
              : "—";
          const details = [
            {
              label: "Amount",
              value: formatAssetValue(amountAsset, order.originalAmount),
              tone: "text-white",
            },
            {
              label: "Remaining",
              value: formatAssetValue(amountAsset, order.remaining),
              tone: "text-sr-neutral-300",
            },
            {
              label: "Price",
              value: formatPrice(order.price),
              tone: "text-white",
            },
          ];

          return (
            <div
              key={order.id.toString()}
              className="rounded-lg border border-white/[0.05] bg-black/10 px-4 py-4"
            >
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="text-base font-medium text-white">{pairLabel}</div>
                  <div
                    className={`mt-1 text-sm font-medium ${
                      isBid ? "text-sr-mint" : "text-sr-salmon"
                    }`}
                  >
                    {isBid ? "Buy" : "Sell"}
                  </div>
                </div>
                <div
                  className={`shrink-0 rounded-full px-2.5 py-1 text-xs font-medium ${
                    statusBadgeStyles[status] ?? "bg-white/5 text-sr-neutral-400"
                  }`}
                >
                  <div className={statusColors[status] ?? "text-sr-neutral-500"}>
                    {statusLabels[status] ?? status}
                  </div>
                </div>
              </div>

              <div className="mt-4 grid grid-cols-2 gap-2">
                {details.map((detail) => (
                  <div
                    key={detail.label}
                    className="rounded-2xl bg-sr-surface px-3 py-2.5"
                  >
                    <div className="text-[10px] uppercase tracking-wider text-sr-neutral-500">
                      {detail.label}
                    </div>
                    <div className="mt-1 text-sm tabular-nums">
                      {renderOrderValue(detail.value, status, detail.tone)}
                    </div>
                  </div>
                ))}
              </div>

              <div className="mt-3 flex items-center justify-between gap-3">
                <div className="min-w-0 text-xs text-sr-neutral-500">
                  {createdAt}
                </div>
                <div className="shrink-0">
                  {renderCancelButton(order.id, status, order.owner)}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Desktop table */}
      <div className="hidden overflow-x-auto md:block">
        <table className="w-full">
          <thead>
            <tr className="text-[10px] text-sr-neutral-500 uppercase tracking-wider">
              <th className="text-left font-medium px-5 py-3">Pair</th>
              <th className="text-left font-medium px-3 py-3">Side</th>
              <th className="text-left font-medium px-3 py-3">Amount</th>
              <th className="text-left font-medium px-3 py-3">Remaining</th>
              <th className="text-left font-medium px-3 py-3">Price</th>
              <th className="text-left font-medium px-3 py-3">Status</th>
              <th className="text-right font-medium px-5 py-3">Created</th>
              <th className="text-right font-medium px-3 py-3"></th>
            </tr>
          </thead>
          <tbody>
            {paginated.map((order) => {
              const market = marketForOrder(order);
              const pairLabel = market
                ? formatMarketPairLabel(market)
                : `${formatAssetLabel(order.pair.base)}/${formatAssetLabel(order.pair.quote)}`;
              const status = order.status?.tag ?? (
                order.remaining === 0n ? "Filled"
                : order.remaining < order.originalAmount ? "PartiallyFilled"
                : "New"
              );
              const isBid = order.side.tag === "Bid";
              // Bids lock quote, asks lock base
              const amountAsset = isBid ? order.pair.quote : order.pair.base;

              return (
                <tr
                  key={order.id.toString()}
                  className="border-t border-sr-border-subtle/50 hover:bg-white/[0.02] transition-colors"
                >
                  <td className="px-5 py-3">
                    <span className="text-sm text-white font-medium">{pairLabel}</span>
                  </td>
                  <td className="px-3 py-3">
                    <span className={`text-sm font-medium ${isBid ? "text-sr-mint" : "text-sr-salmon"}`}>
                      {isBid ? "Buy" : "Sell"}
                    </span>
                  </td>
                  <td className="px-3 py-3">
                    {renderOrderValue(
                      formatAssetValue(amountAsset, order.originalAmount),
                      status,
                      "text-sm text-white tabular-nums",
                    )}
                  </td>
                  <td className="px-3 py-3">
                    {renderOrderValue(
                      formatAssetValue(amountAsset, order.remaining),
                      status,
                      "text-sm text-sr-neutral-300 tabular-nums",
                    )}
                  </td>
                  <td className="px-3 py-3">
                    {renderOrderValue(
                      formatPrice(order.price),
                      status,
                      "text-sm text-white tabular-nums",
                    )}
                  </td>
                  <td className="px-3 py-3">
                    <span className={`text-sm font-medium ${statusColors[status] ?? "text-sr-neutral-500"}`}>
                      {statusLabels[status] ?? status}
                    </span>
                  </td>
                  <td className="px-5 py-3 text-right">
                    <span className="text-sm text-sr-neutral-300 tabular-nums">
                      {order.created > 0n
                        ? new Date(Number(order.created)).toLocaleString()
                        : "—"}
                    </span>
                  </td>
                  <td className="px-3 py-3 text-right">
                    {renderCancelButton(order.id, status, order.owner)}
                  </td>
                </tr>
              );
            })}
            {sorted.length === 0 && (
              <tr>
                <td colSpan={8} className="py-0">
                  <TradingPaneState
                    compact
                    eyebrow={activeTab === "orders" ? "My Orders" : "Market Activity"}
                    title={emptyState.title}
                    message={emptyState.message}
                    action={emptyState.action}
                  />
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between border-t border-sr-border-subtle/50 px-4 py-2">
          <span className="text-[11px] text-sr-neutral-500">
            {page * PAGE_SIZE + 1}–{Math.min((page + 1) * PAGE_SIZE, sorted.length)} of {sorted.length}
          </span>
          <div className="flex gap-1">
            <button
              onClick={() => setPage((p) => Math.max(0, p - 1))}
              disabled={page === 0}
              className="sr-focus-ring rounded-md px-2.5 py-1 text-[11px] font-medium text-sr-neutral-400 transition-colors hover:text-white disabled:opacity-30 disabled:cursor-not-allowed"
            >
              Prev
            </button>
            <button
              onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
              disabled={page >= totalPages - 1}
              className="sr-focus-ring rounded-md px-2.5 py-1 text-[11px] font-medium text-sr-neutral-400 transition-colors hover:text-white disabled:opacity-30 disabled:cursor-not-allowed"
            >
              Next
            </button>
          </div>
        </div>
      )}
    </section>
  );
};
